<!-- The printed activation card: the one code that brings a number back on a
     new phone. Shown once when the first friend is added; re-viewable from
     Settings. IonModal traps focus; this returns it to the opener. -->
<template>
  <IonModal
    :is-open="isOpen"
    :initial-breakpoint="1"
    :breakpoints="[0, 1]"
    class="pm-scr-sheet pm-scr-sheet--auto"
    @will-present="onPresent"
    @did-dismiss="onDismiss"
  >
    <div class="pm-scr-sheet-body">
      <h2 class="section-title">Activation card</h2>

      <div class="pm-scr-card-print" role="group" aria-label="Activation card">
        <div class="pm-scr-card-print-top">
          <span>PAGE ME</span>
          <span>ACTIVATION CARD</span>
        </div>
        <p class="pm-scr-card-print-label">PAGER NUMBER</p>
        <p class="pm-scr-card-print-value">{{ number }}</p>
        <p class="pm-scr-card-print-label">ACTIVATION CODE</p>
        <p v-if="code" class="pm-scr-card-print-code" :aria-label="spelled">{{ code }}</p>
        <p v-else class="pm-scr-card-print-missing">Not on this phone</p>
        <p class="pm-scr-card-print-foot">KEEP THIS CARD. DO NOT SHARE.</p>
      </div>

      <p>Keep your number. If you get a new phone, this code brings your number and friends back.</p>
      <p v-if="!code" class="pm-scr-muted">
        This phone restored its number, so the code was shown on the phone that first got it.
      </p>

      <div class="pm-scr-button-row">
        <IonButton fill="outline" :disabled="!code" @click="copy.run()">Copy</IonButton>
        <IonButton fill="outline" :disabled="!code || save.busy.value" @click="save.run()">
          {{ save.busy.value ? 'Saving…' : 'Save' }}
        </IonButton>
      </div>
      <p v-if="save.error.value" class="field-error" role="alert">{{ save.error.value }}</p>
      <IonButton expand="block" @click="done">Done</IonButton>
    </div>
  </IonModal>
</template>

<script setup>
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { IonModal, IonButton } from '@ionic/vue';
import { useAction } from '../core/ui/index.js';
import { shareUrl, isNative } from '../core/native.js';
import { shareOrDownload } from '../core/export.js';
import { me, markActivationCardShown } from '../store.js';
import { formatNumber } from '../pm/format.js';

const props = defineProps({ isOpen: { type: Boolean, default: false } });
const emit = defineEmits(['close']);

// The sheet belongs to the screen that raised it. Leaving that screen (the
// Friends button, a MODE / SETUP key) closes it — and counts as seen, like a
// swipe-away, so the next screen does not raise it all over again.
const route = useRoute();
watch(() => route?.fullPath, (to, from) => {
  if (!props.isOpen || to === from) return;
  markActivationCardShown();
  emit('close');
});

const number = computed(() => (me.value?.number ? formatNumber(me.value.number) : '—'));
const code = computed(() => me.value?.activationCode || '');
// Read character by character, so "K7QX" is not read as a word.
const spelled = computed(() => `Activation code ${code.value.replace(/-/g, ' ').split('').join(' ')}`);

const cardText = () => [
  'PAGE ME — ACTIVATION CARD',
  `Pager number: ${number.value}`,
  `Activation code: ${code.value}`,
  '',
  'If you get a new phone, open Page Me → Settings → Restore a number and enter this code.',
].join('\n');

const copy = useAction(async () => {
  await navigator.clipboard.writeText(code.value);
}, { done: 'Code copied' });

const save = useAction(async () => {
  const title = 'Page Me activation card';
  if (isNative) {
    const r = await shareUrl(undefined, title, cardText());
    if (r) return;
  }
  if (navigator.share) {
    try { await navigator.share({ title, text: cardText() }); return; }
    catch (e) { if (e?.name === 'AbortError') return; }
  }
  await shareOrDownload(new Blob([cardText()], { type: 'text/plain' }), 'page-me-activation-card.txt', title);
});

function done() {
  markActivationCardShown();
  emit('close');
}

let opener = null;
function onPresent() { opener = document.activeElement; }
function onDismiss() {
  // Swiping it away counts as seen — it is always in Settings.
  markActivationCardShown();
  emit('close');
  const el = opener;
  opener = null;
  try { el?.focus?.(); } catch { /* gone */ }
}
</script>
